import React from "react";
import { Wallet } from "lucide-react";
import { shortAddr } from "./format";

/**
 * PerWalletStake — where the member's stake sits, wallet by wallet (ARC-45).
 *
 * The slot pool is MEMBER-level: the engine sums every verified wallet and the
 * tab gates on that one number (ARC-53). That is the correct gate, and it is
 * also why this panel exists — a member who stakes on a cold wallet and trades
 * from a hot one sees "3 staked" in the header with nothing staked on the
 * wallet in front of them, and has no way to tell whether the engine can see
 * the cold one at all. This is the breakdown behind the total.
 *
 * TWO WALLETS IS THE FLOOR. With one wallet the breakdown is the header count
 * restated in a second place, so it renders nothing — a panel that says the
 * same number twice is noise, and every member has exactly one wallet until
 * ARC-46 ships "Add wallet".
 *
 * DISPLAY ONLY. Nothing here gates a row, changes config, or names a slot as
 * spent — `staking.perWallet` reports where the STAKE is, and the pairs a
 * strategy runs on are not attached to a wallet at all. A null `staked` is the
 * engine declining to count for that wallet and renders as an em dash, never a
 * zero: the same rule slotGate.js holds for the pool as a whole.
 *
 * COMPLIANCE: counts of staked Supernovas and truncated addresses. No return,
 * price, projection or promise appears in this file's copy.
 */

/* One staked Supernova is one automation slot, so the count IS the slot
   contribution — but only where the engine actually counted it. */
const stakedLabel = (staked) => {
  if (typeof staked !== "number" || !Number.isFinite(staked) || staked < 0) return "—";
  return `${staked} staked`;
};

const PerWalletStake = ({ wallets = [] }) => {
  const list = Array.isArray(wallets) ? wallets.filter((w) => w && w.address) : [];

  /* One wallet (or none) ⇒ the header count already says it. See above. */
  if (list.length < 2) return null;

  return (
    <div className="sa-perwallet" data-testid="sa-per-wallet">
      <p className="sa-perwallet-h">
        <Wallet size={12} aria-hidden="true" />
        Where your stake sits
      </p>

      <ul className="sa-perwallet-list">
        {list.map((w) => (
          <li key={w.address} className="sa-perwallet-row" data-testid="sa-per-wallet-row">
            {/* The label is the member's own name for the wallet where the
                engine has one; the address is what proves WHICH wallet, so it
                stays on screen either way. */}
            <span className="sa-perwallet-who">
              {w.label ? <span className="sa-perwallet-label">{w.label}</span> : null}
              <span className="sa-perwallet-addr" title={w.address}>
                {shortAddr(w.address)}
              </span>
            </span>
            <span className="sa-perwallet-n tnum">{stakedLabel(w.staked)}</span>
          </li>
        ))}
      </ul>

      <p className="sa-perwallet-note">
        Your slots are pooled across every wallet you have verified — a strategy
        does not have to run on the wallet holding the stake behind it.
      </p>
    </div>
  );
};

export default PerWalletStake;
